import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { getFeedSnaps, getVotedSnaps } from '@/lib/api';
import { castVote, removeVote } from '@/lib/api/votes';
import { toggleLike } from '@/lib/api/likes';
import type { Snap } from '@/types/database.types';
import { getActivityThresholds, getSnapActivity } from '@/utils/snap';

// Import refactored components
import SnapFeedHeader from '@/components/feed/SnapFeedHeader';
import SnapFeedNavigation from '@/components/feed/SnapFeedNavigation';
import SnapFeedCard from '@/components/feed/SnapFeedCard';
import SnapFeedEmpty from '@/components/feed/SnapFeedEmpty';
import SnapFeedSwipeHint from '@/components/feed/SnapFeedSwipeHint';
import ShareModal from '@/components/ShareModal';

export default function SnapFeed() {
  const [snaps, setSnaps] = useState<Snap[]>([]);
  const [votedSnaps, setVotedSnaps] = useState<Snap[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingVoted, setLoadingVoted] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [votedPage, setVotedPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasMoreVoted, setHasMoreVoted] = useState(true);
  const [isVoting, setIsVoting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showVoted, setShowVoted] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const controls = useAnimation();
  const navigate = useNavigate();
  const snapContainerRef = useRef<HTMLDivElement>(null);

  // Fetch initial snaps
  useEffect(() => {
    fetchSnaps();
    fetchVotedSnaps();
    
    // Hide body overflow to prevent scrolling behind the modal
    document.body.style.overflow = 'hidden';
    
    // Restore overflow on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Hide swipe hint after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSwipeHint(false);
    }, 5000);
    
    return () => clearTimeout(timer);
  }, []);

  const fetchSnaps = async (newPage = page) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getFeedSnaps(newPage, 10);
      
      if (newPage === 1) {
        setSnaps(response.data);
      } else {
        setSnaps(prev => [...prev, ...response.data]);
      }
      
      setHasMore(response.data.length > 0 && response.total > newPage * 10);
      setPage(newPage);
    } catch (error) {
      console.error('Error fetching SNAPs:', error);
      setError('Failed to load SNAPs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchVotedSnaps = async (newPage = votedPage) => {
    try {
      setLoadingVoted(true);
      
      const response = await getVotedSnaps(newPage, 10);
      
      if (newPage === 1) {
        setVotedSnaps(response.data);
      } else {
        setVotedSnaps(prev => [...prev, ...response.data]);
      }
      
      setHasMoreVoted(response.data.length > 0 && response.total > newPage * 10);
      setVotedPage(newPage);
    } catch (error) {
      console.error('Error fetching voted SNAPs:', error);
    } finally {
      setLoadingVoted(false);
    }
  };

  // Load more snaps when we're close to the end
  useEffect(() => {
    if (!showVoted && !loading && hasMore && currentIndex >= snaps.length - 3) {
      fetchSnaps(page + 1);
    } else if (showVoted && !loadingVoted && hasMoreVoted && currentIndex >= votedSnaps.length - 3) {
      fetchVotedSnaps(votedPage + 1);
    }
  }, [currentIndex, snaps.length, votedSnaps.length, loading, loadingVoted, hasMore, hasMoreVoted, showVoted, page, votedPage]);

  // Calculate activity thresholds and add activity property to snaps
  const thresholds = getActivityThresholds([...snaps, ...votedSnaps]);
  
  const currentArray = showVoted ? votedSnaps : snaps;
  const currentSnap = currentArray[currentIndex];
  
  // Add activity property to current snap
  const currentSnapWithActivity = currentSnap ? {
    ...currentSnap,
    activity: getSnapActivity(currentSnap, thresholds)
  } : null;

  const handleVote = async (optionId: string) => {
    if (!currentSnap || isVoting) return;
    
    // Don't allow voting on the same option again
    if (currentSnap.userVote === optionId) {
      toast.info('You have already voted for this option');
      return;
    }
    
    try {
      setIsVoting(true);
      
      // Call the API to cast vote
      await castVote(currentSnap.id, optionId);
      
      // Refresh both snap lists to ensure data consistency
      await Promise.all([
        fetchSnaps(1),
        fetchVotedSnaps(1)
      ]);
      
      // Reset current index to 0 for the voted view
      if (showVoted) {
        setCurrentIndex(0);
      } else {
        // If we're in the unvoted view, move to the next snap
        if (currentIndex < snaps.length - 1) {
          setCurrentIndex(currentIndex + 1);
        } else if (snaps.length > 1) {
          setCurrentIndex(0);
        } else {
          // If this was the last snap, switch to voted view
          setShowVoted(true);
          setCurrentIndex(0);
        }
      }
      
      toast.success('Vote cast successfully!');
    } catch (error) {
      console.error('Error casting vote:', error);
      toast.error('Failed to cast vote');
    } finally {
      setIsVoting(false);
    }
  };

  const handleRemoveVote = async () => {
    if (!currentSnap || isVoting || !currentSnap.userVote) return;
    
    try {
      setIsVoting(true);
      
      // Call the API to remove vote
      await removeVote(currentSnap.id);
      
      // Refresh both snap lists to ensure data consistency
      await Promise.all([
        fetchSnaps(1),
        fetchVotedSnaps(1)
      ]);
      
      // If we're in the voted view and this was the last snap, go back to unvoted view
      if (showVoted && votedSnaps.length <= 1) {
        setShowVoted(false);
        setCurrentIndex(0);
      } else if (showVoted) {
        // If we're in voted view, stay there but reset index if needed
        setCurrentIndex(prev => Math.min(prev, votedSnaps.length - 2));
      }
      
      toast.success('Vote removed successfully!');
    } catch (error) {
      console.error('Error removing vote:', error);
      toast.error('Failed to remove vote');
    } finally {
      setIsVoting(false);
    }
  };

  const handleLike = async (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering parent click events
    
    if (!currentSnap) return;
    
    try {
      // Use the centralized like handling function
      const { liked, count } = await toggleLike(currentSnap.id);
      
      // Update the appropriate array based on which view we're in
      if (showVoted) {
        setVotedSnaps(prev => 
          prev.map(snap => 
            snap.id === currentSnap.id 
              ? { 
                  ...snap, 
                  likes_count: count,
                  userLiked: liked
                } 
              : snap
          )
        );
      } else {
        setSnaps(prev => 
          prev.map(snap => 
            snap.id === currentSnap.id 
              ? { 
                  ...snap, 
                  likes_count: count,
                  userLiked: liked
                } 
              : snap
          )
        );
      }
      
      toast.success(liked ? 'SNAP liked' : 'SNAP unliked');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to update like');
      }
    }
  };

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: any) => {
    setIsDragging(false);
    
    const threshold = 100; // minimum distance required for a swipe
    
    if (Math.abs(info.offset.y) > threshold) {
      // Swipe up (next)
      if (info.offset.y < 0) {
        controls.start({ 
          y: -window.innerHeight, 
          opacity: 0,
          transition: { duration: 0.3 }
        }).then(goToNextSnap);
      } 
      // Swipe down (previous)
      else {
        controls.start({ 
          y: window.innerHeight, 
          opacity: 0,
          transition: { duration: 0.3 }
        }).then(goToPreviousSnap);
      }
    } else {
      // Return to center if not swiped far enough
      controls.start({ y: 0, opacity: 1, transition: { duration: 0.3 } });
    }
  };

  const goToNextSnap = () => {
    const currentArray = showVoted ? votedSnaps : snaps;
    
    if (currentIndex < currentArray.length - 1) {
      setCurrentIndex(prev => prev + 1);
      controls.set({ y: 0, opacity: 1 });
    } else if ((showVoted && hasMoreVoted) || (!showVoted && hasMore)) {
      // If we're at the end but there are more snaps to load
      toast.success('Loading more SNAPs...');
      if (showVoted) {
        fetchVotedSnaps(votedPage + 1);
      } else {
        fetchSnaps(page + 1);
      }
    } else {
      // If we're at the end and there are no more snaps
      toast.success('You have seen all available SNAPs!');
      setCurrentIndex(0); // Loop back to the beginning
      controls.set({ y: 0, opacity: 1 });
    }
  };

  const goToPreviousSnap = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      controls.set({ y: 0, opacity: 1 });
    } else {
      // If we're at the beginning, stay there or loop to the end
      const currentArray = showVoted ? votedSnaps : snaps;
      if (currentArray.length > 0) {
        setCurrentIndex(currentArray.length - 1);
        controls.set({ y: 0, opacity: 1 });
      }
    }
  };

  const toggleMode = () => {
    setShowVoted(!showVoted);
    setCurrentIndex(0);
    controls.set({ y: 0, opacity: 1 });
  };

  const handleClose = () => {
    navigate('/dashboard');
  };

  const handleShare = () => {
    if (currentSnap) {
      setShowShareModal(true);
    }
  };

  // Loading state
  if ((loading && snaps.length === 0) || (showVoted && loadingVoted && votedSnaps.length === 0)) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  // Error state
  if (error && snaps.length === 0 && !showVoted) {
    return (
      <SnapFeedEmpty 
        showVoted={showVoted}
        hasVotedSnaps={votedSnaps.length > 0}
        onToggleMode={toggleMode}
        onClose={handleClose}
        error={error}
        onRetry={() => fetchSnaps(1)}
      />
    );
  }

  // Empty states
  if (snaps.length === 0 && !showVoted) {
    return (
      <SnapFeedEmpty 
        showVoted={showVoted}
        hasVotedSnaps={votedSnaps.length > 0}
        onToggleMode={toggleMode}
        onClose={handleClose}
      />
    );
  }

  if (votedSnaps.length === 0 && showVoted) {
    return (
      <SnapFeedEmpty 
        showVoted={showVoted}
        hasVotedSnaps={false}
        onToggleMode={toggleMode}
        onClose={handleClose}
      />
    );
  }

  if (currentArray.length === 0) {
    return (
      <SnapFeedEmpty 
        showVoted={showVoted}
        hasVotedSnaps={votedSnaps.length > 0}
        onToggleMode={toggleMode}
        onClose={handleClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/90 overflow-hidden">
      <SnapFeedHeader 
        showVoted={showVoted} 
        onClose={handleClose}
        onShare={handleShare}
      />
      
      <div className="relative h-screen w-full overflow-hidden" ref={snapContainerRef}>
        {currentSnapWithActivity && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center p-4 pb-8" // pb for bottom padding to make room for navigation
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.7}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={handleDragEnd}
            animate={controls}
            initial={{ y: 0, opacity: 1 }}
          >
            <div 
              className="relative w-full max-w-md bg-white rounded-xl shadow-xl overflow-hidden"
              style={{ 
                height: 'calc(100vh - 150px)', // Position                
                maxWidth: window.innerWidth < 640 ? '100%' : '28rem' // Full width on small screens
              }}
            >
              <SnapFeedCard 
                snap={currentSnapWithActivity} 
                onVote={handleVote}
                onRemoveVote={handleRemoveVote}
                onLike={handleLike}
                onShare={handleShare}
              />
              
              <SnapFeedSwipeHint show={showSwipeHint} />
            </div>
          </motion.div>
        )}
        
        <SnapFeedNavigation 
          currentIndex={currentIndex}
          totalSnaps={currentArray.length}
          onPrevious={goToPreviousSnap}
          onNext={goToNextSnap}
          controls={controls}
        />
      </div>

      {currentSnap && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          snapId={currentSnap.id}
          snapTitle={currentSnap.title}
          snapDescription={currentSnap.description}
        />
      )}
    </div>
  );
}